import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import { RootStore } from 'store/rootStore';
import { formUtils } from '@insurancemenu/imenu-components-lib';
import { FormObjectFromJSON } from '@insurancemenu/imenu-components-lib/dist/types';
import {
  DDARAdditionalContact,
  DDARAdditionalContactInformation,
  DDARCustomUser,
  DDIAAffiliateSubsidiary,
  DDIAAnotherContract,
  DDIANotActivelyAtWorkEE,
  FormInstanceType,
  ZywaveBravoHealthImqDoctor,
  ZywaveBravoHealthImqMedication,
  ZywaveMemorialHermannCompensationEE,
  ZywaveMemorialHermannExemptEE,
} from 'types/formService';
import {
  CustomFieldControl,
  DynamicCustomFieldsDefaultType,
  DynamicFormController,
} from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { requestEmployerFormInstance, requestSetAnswers } from 'api/operations/formService';
import EnrollmentPeriodModule from 'store/modules/enrollment/enrollmentPeriodModule';
import { isIncorporationDateValid } from 'global/utils/sponsor-validations/sunLife/incorporationDateValidation';
import { DDARAdditionalContactsInformationDelegate } from 'store/modules/dynamic-form/DDAR/additionalContactsInformationDelegate';

export interface WebFormCustomFields extends DynamicCustomFieldsDefaultType {
  authorizedUsers: CustomFieldControl<DDARCustomUser[]>;
  textWithCompanyName: CustomFieldControl<string>;
  termsAndConditions: CustomFieldControl<string>;
  affiliateSubsidiary: CustomFieldControl<DDIAAffiliateSubsidiary[]>;
  anotherContract: CustomFieldControl<DDIAAnotherContract[]>;
  notActivelyAtWorkEE: CustomFieldControl<DDIANotActivelyAtWorkEE[]>;
  memorialHermannExemptEE: CustomFieldControl<ZywaveMemorialHermannExemptEE[]>;
  memorialHermannCompensationEE: CustomFieldControl<ZywaveMemorialHermannCompensationEE[]>;
  bravoHealthImqDoctor: CustomFieldControl<ZywaveBravoHealthImqDoctor[]>;
  bravoHealthImqMedication: CustomFieldControl<ZywaveBravoHealthImqMedication[]>;
  ddarAdditionalContacts: CustomFieldControl<DDARAdditionalContact[]>;
  incorporationDate: CustomFieldControl<string>;
  ddarAdditionalContactsInformation: CustomFieldControl<DDARAdditionalContactInformation[]>;
}

export default class WebFormStore {
  rootStore: RootStore;
  formInstance: null | FormInstanceType;
  dynamicForm: null | formUtils.DynamicFormController<WebFormCustomFields>;
  loading = false;
  private _enrollmentPeriodModule = new EnrollmentPeriodModule();

  public ddarAdditionalContactsInformationDelegate: DDARAdditionalContactsInformationDelegate | null = null;

  setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  get effectiveDate(): string {
    return this._enrollmentPeriodModule.enrollmentCoreData?.effectiveDate || '';
  }

  createFormController(formObject: FormObjectFromJSON): void {
    this.setLoading(true);
    this.dynamicForm = new DynamicFormController<WebFormCustomFields>(formObject, this.rootStore.userStore.userRoles);
    this.setLoading(false);
  }

  private _prepopulateIncorporationDate(): void {
    const field = this.dynamicForm?.customFields.incorporationDate;

    const incorporationDate =
      this._enrollmentPeriodModule.enrollmentProposalData?.confirmedProposal?.browsingCriteria?.incorporationDate;

    if (!field || field.value || !incorporationDate) {
      return;
    }

    field.updateValue(incorporationDate);
  }

  /** Set custom-field initial values (e.g. from cart), then overwrite with saved user answers. */
  private _populateCustomFields(): void {
    if (!this.dynamicForm || !this.formInstance) {
      return;
    }

    this._prepopulateIncorporationDate();
    this.dynamicForm.populateCustomFields(this.formInstance.answers.custom as unknown as Record<string, string>);

    // Validate populated values
    const incorporationDateField = this.dynamicForm.customFields.incorporationDate;
    if (incorporationDateField) {
      incorporationDateField.updateValidationStatus(
        isIncorporationDateValid(incorporationDateField.value, this.effectiveDate),
      );
    }
  }

  async getFormInstance(formInstanceId: string, periodId: string): Promise<void> {
    this.setLoading(true);
    this.formInstance = null;
    this.dynamicForm = null;
    this.ddarAdditionalContactsInformationDelegate = null;
    const [res] = await Promise.all([
      requestEmployerFormInstance(formInstanceId),
      this._enrollmentPeriodModule.uploadEnrollmentPeriod(periodId),
    ]);

    runInAction(() => {
      this.formInstance = res.data.formInstanceById;
      this.setLoading(false);
    });
  }

  async setAnswers(complete: boolean): Promise<void> {
    if (this.formInstance && this.dynamicForm) {
      try {
        this.setLoading(true);
        const res = await requestSetAnswers({
          formInstanceId: this.formInstance.id,
          answers: {
            common: this.dynamicForm.getCanonicalAnswers(),
            custom: this.dynamicForm.getCustomFormFieldsObject(),
          },
          complete,
        });
        runInAction(() => {
          if (res.data) {
            this.formInstance = res.data.setAnswers;
          }
        });
      } finally {
        this.setLoading(false);
      }
    }
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.dynamicForm = null;
    this.formInstance = null;

    makeObservable(this, {
      dynamicForm: observable,
      loading: observable,
      formInstance: observable,
      ddarAdditionalContactsInformationDelegate: observable,
      effectiveDate: computed,
      setLoading: action.bound,
      createFormController: action.bound,
      setAnswers: action.bound,
      getFormInstance: action.bound,
    });

    reaction(
      () => this.formInstance,
      (formInstance) => {
        if (formInstance) {
          this.createFormController(formInstance.form);
        }
      },
    );

    reaction(
      () => this.dynamicForm,
      (dynamicForm) => {
        return (
          dynamicForm &&
          dynamicForm.populateFields(this.formInstance!.answers.common as unknown as Record<string, string>)
        );
      },
    );

    reaction(
      () => this.dynamicForm,
      (dynamicForm) => {
        if (dynamicForm) {
          this._populateCustomFields();
          this.ddarAdditionalContactsInformationDelegate = new DDARAdditionalContactsInformationDelegate(dynamicForm);
        }
      },
    );
  }
}
